import { FarmRiskType } from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

// "Check all that apply" options for the commercial/income-producing use question (#33923).
export const commercialUseOptions = [
  { id: 'dwellingHome', value: 'Dwelling/Home' },
  { id: 'swimmingPool', value: 'Swimming Pool' },
  { id: 'pickleballCourt', value: 'Pickleball Court' },
  { id: 'basketballCourt', value: 'Basketball Court' },
  { id: 'eventWeddingVenue', value: 'Event/Wedding Venue' },
  { id: 'barnOutbuilding', value: 'Barn/Outbuilding' },
  { id: 'recreationalLand', value: 'Recreational Land' },
  { id: 'other', value: 'Other' }
];

export const eligibilityQuestions = () => [
  {
    id: 'unrepairedDamage',
    question:
      'Is there any unrepaired damage or history of water damage claims on any dwelling or structure on this application?'
  },
  {
    id: 'priorFireLoss',
    question:
      'Has any applicant or household member sustained a prior fire loss, or has there been a fire on any premise in the last 10 years?'
  },
  {
    id: 'priorLiabilityLoss',
    question:
      'Has any applicant or household member sustained a prior liability loss, or had a claim made against them?'
  },
  {
    id: 'chargedWithFelony',
    question: 'Has any applicant or household member ever been charged with a felony?'
  },
  {
    id: 'foreclosureBankruptcy',
    question:
      'Has the applicant had a foreclosure, repossession, bankruptcy, or filed for bankruptcy during the past 5 years?'
  },
  {
    id: 'otherInsuranceWithCompany',
    question: 'Any other insurance with this company?',
    label: 'List line(s) of business and policy numbers'
  }
];

export const propertyRiskFactorQuestions = (selectedOption) => {
  const isHome = selectedOption === policyTypes.FarmHome;
  const isRental = selectedOption === policyTypes.FarmRental;
  // Home (Z) and Rental (Y) must not say "farm" to staff/customers, so the rented-to-others
  // question drops the "farm barns" wording. (#31217 defarming, mirrors Forreston.)
  const useDefarmedWording = isHome || isRental;

  const questions = [
    {
      id: 'otherResidences',
      question:
        'Does the applicant have any other residences that are not listed on this application, whether owned, occupied, or rented?',
      label: 'Provide address and occupancy details (owned, occupied, or rented) for all offlisted residences'
    },
    {
      id: 'unlistedStructures',
      question: 'Are there any structures on the premises that are not listed or that should be excluded?',
      label:
        'Provide details of all unlisted structures on the premises, and list of any structures the applicant wants to exclude with the reason for each exclusion'
    },
    {
      id: 'recreationalVehicles',
      question: 'Are there any recreational vehicles the applicant owns that are not included in this application?',
      tooltip: 'Including but not limited to, snow mobiles, dune buggies, mini bikes, ATVs, etc.',
      label: 'Provide the type, make, models and usage details for all unlisted recreational vehicles'
    },
    {
      id: 'livestockFencing',
      question: 'Is the fencing for livestock on the premises in good repair?',
      tooltip: 'If there are no livestock on the premise, please select "Yes."',
      label: 'Provide a description of condition and type of fencing used for livestock on premises',
      showDetailsWhen: ['false', false]
    },
    {
      id: 'livestockEscape',
      question: 'Has there ever been an incident of livestock escape?',
      tooltip: 'If there are no livestock on the premise, please select "No"',
      label: 'Provide details of each livestock escape incident'
    },
    {
      id: 'dwellingsUnderRenovation',
      question: 'Are there any dwellings/structures under renovation or construction?',
      label: 'Provide details about the dwellings or structures under renovation or construction'
    },
    {
      id: 'farmStructuresRentedToOthers',
      question: useDefarmedWording
        ? 'Are there any buildings or structures on the premises rented to others?'
        : 'Are there any farm barns, buildings, or structures on the premises rented to others?',
      label: useDefarmedWording
        ? 'Provide a description of each building or structure rented to others'
        : 'Provide a description of each barn, building, or structure rented to others'
    }
  ];

  // Home (Z) has no livestock, so the two livestock questions drop. Rental (Y) additionally drops rec
  // vehicles and structures-rented-to-others — none apply to a rental risk (#34326).
  const suppressedIds = isRental
    ? ['recreationalVehicles', 'livestockFencing', 'livestockEscape', 'farmStructuresRentedToOthers']
    : isHome
      ? ['livestockFencing', 'livestockEscape']
      : [];

  return questions.filter((q) => !suppressedIds.includes(q.id));
};

export const operationalDetailsQuestions = (selectedOption) => {
  // Anhydrous-ammonia and custom-farming are farm-operations questions that don't apply to Home (Z)
  // or Rental (Y), matching Forreston and the property-risk section's Home/Rental handling.
  const isHomeOrRental = selectedOption === policyTypes.FarmHome || selectedOption === policyTypes.FarmRental;

  const questions = [
    {
      id: 'commercialUseOfProperty',
      type: 'commercialUse',
      question:
        'Is any part of the insured property used for a business, short-term rentals, event rentals, recreational rentals, or any other commercial/income-producing purpose?',
      options: commercialUseOptions,
      label: 'Provide details about each item selected'
    },
    {
      id: 'businessConductedOnPremises',
      question: 'Is any business conducted on the premises?',
      tooltip: 'Including but not limited to, farming, home office or business, telecommuters, etc.',
      label: 'Provide details about each type of business conducted on the premises'
    },
    {
      id: 'dayCareBusiness',
      question: 'Is there any day care business conducted on the premises?',
      label: 'Provide details related to the day care, and include the number of children'
    },
    {
      id: 'anhydrousAmmoniaEquipment',
      question:
        'Does any insured own anhydrous ammonia or weed spraying equipment for use on the premises or any other property?'
    },
    {
      id: 'processOrManufacture',
      question: 'Does the applicant process or manufacture any of their own products?'
    },
    {
      id: 'huntingOrFishingFee',
      question: 'Does the applicant allow hunting or fishing for a fee on any premises?'
    },
    {
      id: 'customFarming',
      question: 'Does the applicant do any custom farming?',
      label:
        "Provide details related to total acres custom farmed, total receipts, any prior liability claims, and if any custom farming is done outside of the insured's state"
    }
  ];

  return isHomeOrRental
    ? questions.filter((q) => !['anhydrousAmmoniaEquipment', 'customFarming'].includes(q.id))
    : questions;
};

// Risk-level questions render as a fourth section at the end of the UW form (ticket #32176). Per-
// risk specifics are captured in the details free-text field — the framework only supports
// policy-level answers today.
const riskLevelQuestionsList = [
  {
    id: 'dogsOrExoticPets',
    question: 'Are there any dogs or exotic pets kept on premises?',
    label:
      'Provide the type of each exotic pet, or if dog, include the bite history and a brief explanation of any biting incidents'
  },
  {
    id: 'dwellingsForSale',
    question: 'Are there any dwellings or homes for sale that are listed on this application?',
    label: 'Provide addresses of the dwellings or homes for sale, along with any liens related to the sale'
  },
  {
    id: 'trampoline',
    question: 'Is there a trampoline on the premises?',
    label:
      'Provide the amount of trampolines, along with details about the presence of safety nets and jump guards for each'
  },
  {
    // Details (owner's name) shown when the named insured is NOT the property owner.
    id: 'namedInsuredIsPropertyOwner',
    question: 'Is the named insured the owner of the property?',
    label: "Owner's name",
    showDetailsWhen: ['false', false]
  }
];

// Dwelling-scoped risk-level questions only apply when the offer has at least one dwelling-bearing
// risk, so they're suppressed when every risk on the offer is a type that insures no residence:
// FO6-Structures (Cov E) covers barns/outbuildings only, and standalone inland marine insures
// scheduled personal property with no building at all (#33164 —
// docs/farm-standalone-inland-marine.md). Asking an inland-marine-only offer about dwellings for sale
// or a trampoline on the premises makes no sense — there are no premises on the policy.
const DWELLING_SCOPED_RISK_LEVEL_IDS = ['dwellingsForSale', 'trampoline'];

const NON_DWELLING_RISK_TYPES = [FarmRiskType.Fo6Structures, FarmRiskType.InlandMarine];

const hasNoDwellingBearingRisk = (riskTypes) =>
  riskTypes.length > 0 && riskTypes.every((type) => NON_DWELLING_RISK_TYPES.includes(type));

export const riskLevelQuestions = (_selectedOption, { riskTypes = [] } = {}) =>
  hasNoDwellingBearingRisk(riskTypes)
    ? riskLevelQuestionsList.filter((q) => !DWELLING_SCOPED_RISK_LEVEL_IDS.includes(q.id))
    : riskLevelQuestionsList;

const ENDORSEMENT_UW_QUESTION_IDS = ['unrepairedDamage', 'dwellingsForSale'];

const allQuestions = [
  ...eligibilityQuestions(),
  ...propertyRiskFactorQuestions(policyTypes.Farm),
  ...operationalDetailsQuestions(policyTypes.Farm),
  ...riskLevelQuestionsList
];

export const endorsementUWQuestions = ENDORSEMENT_UW_QUESTION_IDS.map((id) =>
  allQuestions.find((question) => question.id === id)
);

export const getFarmUWQuestionSections = (selectedOption, { riskTypes = [] } = {}) => ({
  eligibilityQuestions: eligibilityQuestions(),
  propertyRiskFactorQuestions: propertyRiskFactorQuestions(selectedOption),
  operationalDetailsQuestions: operationalDetailsQuestions(selectedOption),
  riskLevelQuestions: riskLevelQuestions(selectedOption, { riskTypes })
});

export default {
  eligibilityQuestions,
  propertyRiskFactorQuestions,
  operationalDetailsQuestions,
  riskLevelQuestions,
  endorsementUWQuestions,
  getFarmUWQuestionSections
};
